import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { VoucherDetails, VoucherDetailsParams } from "./types";
import { fetchVoucherListApi } from "./VoucherService";

// Voucher type based on API response
export interface Voucher {
  voucherId: number;
  programCode: string;
  reconStatus: number;
  reconDisplayStatus: string;
  reconDate: string | null;
  reconMessage: string | null;
  updatedAt: string;
  createdAt: string;
  saralId: string;
  code: string | null;
  amount: string;
  expiryDate: string;
  voucherCreateDate: string | null;
  state: string;
  bankCreateResponse: string | null;
  availableAmount: number;
  "voucher_request.referenceId": string;
  distributorId?: number | null;
  distributorName?: string | null;
}

export interface VoucherListResponse {
  vouchers: Voucher[];
  page: number;
  totalCount: number;
  append?: boolean;
}

// Interface for voucher list request parameters
type VoucherStateFilter =
  | "activated"
  | "failed"
  | "issued"
  | "used"
  | "revoked"
  | "exhausted"
  | "expired";

export interface VoucherListParams {
  limit: number;
  page: number;
  program?: number | number[];
  distributor?: number | number[];
  channel?: number | number[];
  processor?: number | number[];
  state?: VoucherStateFilter | VoucherStateFilter[];
  recon?: number | number[];
  search?: string;
  fromDate?: string;
  toDate?: string;
  dateField?: string;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  timezone?: string;
  append?: boolean;
}

export interface fetchVoucherParam {
  limit: number;
  page: number;
  program?: number | number[];
  distributor?: number | number[];
  channel?: number | number[];
  processor?: number | number[];
  state?: VoucherStateFilter | VoucherStateFilter[];
  recon?: number | number[];
  search?: string;
  fromDate?: string;
  toDate?: string;
  dateField?: string;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  timezone?: string;
  append?: boolean;
}

export const fetchVouchers = createAsyncThunk(
  "voucher/fetchVouchers",
  async (
    params: VoucherListParams,
    { rejectWithValue }
  ) => {
    try {
      return await fetchVoucherListApi(params);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch vouchers"
      );
    }
  }
);


export const fetchVoucherDetails = createAsyncThunk(
  "voucher/fetchVoucherBySaralCode",
  async (
    params: VoucherDetailsParams,
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem("accessToken");
    try {

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/vouchers/fetchVoucherBySaralCode`,
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        data: {
          saralcode: params.saralcode,
        },
      });
      return { ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch vouchers"
      );
    }
  }
);

export const revokeVoucher = createAsyncThunk(
  "voucher/revokeVoucher",
  async (
    params: { saralCode: string },
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/vouchers/revoke`,
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        data: {
          saralCode: params.saralCode,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to revoke voucher"
      );
    }
  }
);

/** Response from PII request-access API. PII is never stored in Redux. */
export interface RequestPiiAccessResponse {
  entity_name: string;
  entity_id: number;
  pii: Record<string, unknown>;
}

export const requestPiiAccess = createAsyncThunk(
  "voucher/requestPiiAccess",
  async (
    params: { entity_name: string; entity_id: number; request_context: string; fields: string[] },
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request<RequestPiiAccessResponse>({
        baseURL: `${window.env.API_URL}/api/v1/pii/request-access`,
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        method: "POST",
        data: {
          entity_name: params.entity_name,
          entity_id: params.entity_id,
          request_context: params.request_context,
          fields: params.fields,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to access PII"
      );
    }
  }
);

interface VoucherState {
  vouchers: Voucher[];
  page: number;
  totalCount: number;
  loading: boolean;
  error: string | null;
  eRupiVoucher: VoucherDetails | null;
  eRupiVoucherLoading: boolean;
  eRupiVoucherError: any | null;
  revokeLoading: boolean;
  revokeError: string | null;
}

const initialState: VoucherState = {
  vouchers: [],
  page: 1,
  totalCount: 0,
  loading: false,
  error: null,
  eRupiVoucher: null,
  eRupiVoucherError: null,
  eRupiVoucherLoading: false,
  revokeLoading: false,
  revokeError: null,
};

const voucherSlice = createSlice({
  name: "voucher",
  initialState,
  reducers: {
    clearVouchers(state) {
      state.vouchers = [];
      state.page = 1;
      state.totalCount = 0;
      state.loading = false;
      state.error = null;
    },
    clearRevokeError(state) {
      state.revokeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVouchers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchVouchers.fulfilled,
        (state, action: PayloadAction<VoucherListResponse>) => {
          state.loading = false;
          if (action.payload.append) {
            // Append new vouchers
            state.vouchers = [...state.vouchers, ...action.payload.vouchers];
          } else {
            // Replace vouchers
            state.vouchers = action.payload.vouchers;
          }
          state.page = action.payload.page;
          state.totalCount = action.payload.totalCount;
        }
      )
      .addCase(fetchVouchers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch vouchers";
      })
      .addCase(fetchVoucherDetails.pending, (state) => {
        state.eRupiVoucherLoading = true;
        state.eRupiVoucherError = null;
      })
      .addCase(fetchVoucherDetails.fulfilled, (state, action: PayloadAction<VoucherDetails>) => {
        state.eRupiVoucherLoading = false;
        state.eRupiVoucher = action.payload;
        state.eRupiVoucherError = null;
      })
      .addCase(fetchVoucherDetails.rejected, (state, action) => {
        state.eRupiVoucherLoading = false;
        state.eRupiVoucher = null;
        state.eRupiVoucherError = action.payload as string || "Failed to fetch voucher details";
      })
      .addCase(revokeVoucher.pending, (state) => {
        state.revokeLoading = true;
        state.revokeError = null;
      })
      .addCase(revokeVoucher.fulfilled, (state) => {
        state.revokeLoading = false;
        state.revokeError = null;
      })
      .addCase(revokeVoucher.rejected, (state, action) => {
        state.revokeLoading = false;
        state.revokeError = action.payload as string || "Failed to revoke voucher";
      })
  },
});

export const { clearVouchers, clearRevokeError } = voucherSlice.actions;
export default voucherSlice.reducer;
